'use client';

/**
 * Upload-activity signal bridge.
 *
 * An unattended long upload generates none of the DOM events
 * (`mousedown`/`keydown`/`touchstart`/`scroll`) that `useIdleTimeout`
 * treats as activity, so the CJIS 30-min idle lock (`providers.tsx`
 * `lockSession()` -> `Clear-Site-Data`) fires mid-upload, wipes the
 * Firebase auth state in IndexedDB, and strands the in-flight upload in
 * `pending_upload` (gh #877). This module lets the Uppy layer report
 * in-flight progress as activity so the idle timer is held off WHILE
 * bytes are actively moving.
 *
 * Decoupling: the Uppy hook (`useFileUpload`) and the idle timer
 * (`useIdleTimeout`, consumed in `providers.tsx`) live far apart in the
 * tree, so the signal rides a plain `window` CustomEvent rather than a
 * shared context — no provider re-wiring, trivially testable.
 *
 * CJIS AC-11 note (see CJIS_EXCEPTIONS.md PE-27): this is deliberately
 * gated on *active* progress, not on "an upload exists". The signal is
 * throttled and only emitted from `upload`/`upload-progress`, which stop
 * the instant a batch stalls, errors, or completes — so a stalled or
 * finished batch resumes the normal idle countdown and still locks on
 * schedule. The lock is never disabled or lengthened; the 12h absolute
 * session cap (IA-11) bounds everything. Worst-case relaxation is ~30 min
 * of unlocked idle after the last byte moved.
 */

export const UPLOAD_ACTIVITY_EVENT = 'foltrigg:upload-activity';

// Progress events fire many times per second; one markActive() pulse per
// this interval is more than enough to keep a 30-min idle timer from
// firing while leaving the throttle cheap.
const SIGNAL_THROTTLE_MS = 15_000;

let lastSignal = 0;

/**
 * Emit an upload-activity pulse (throttled). Safe to call on every
 * progress event — at most one window event fires per SIGNAL_THROTTLE_MS.
 */
export function signalUploadActivity(): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - lastSignal < SIGNAL_THROTTLE_MS) return;
  lastSignal = now;
  window.dispatchEvent(new Event(UPLOAD_ACTIVITY_EVENT));
}

/**
 * Subscribe to upload-activity pulses. Returns an unsubscribe fn.
 */
export function onUploadActivity(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(UPLOAD_ACTIVITY_EVENT, callback);
  return () => window.removeEventListener(UPLOAD_ACTIVITY_EVENT, callback);
}
